<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import EnterpriseAccountAPI from 'dashboard/api/enterprise/account';
import ButtonV4 from 'next/button/Button.vue';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHistory from './components/BillingHistory.vue';
import BillingHeader from './components/BillingHeader.vue';
import BillingMeter from './components/BillingMeter.vue';
import CreditBalanceBreakdown from './components/CreditBalanceBreakdown.vue';
import SubscriptionSummary from './components/SubscriptionSummary.vue';

const { t } = useI18n();

const billingData = ref(null);
const transactions = ref([]);
const transactionPagination = ref({
  current_page: 1,
  per_page: 25,
  total_pages: 0,
  total_count: 0,
});
const isLoading = ref(true);
const isHistoryLoading = ref(false);
const hasLoadError = ref(false);

const subscription = computed(() => billingData.value?.subscription || null);

const usageMeters = computed(() => {
  const usage = billingData.value?.usage || {};
  const capacityMeters = [
    ['users', t('BILLING_SETTINGS.USAGE.USERS')],
    ['inboxes', t('BILLING_SETTINGS.USAGE.INBOXES')],
    ['documents', t('BILLING_SETTINGS.USAGE.DOCUMENTS')],
  ].map(([key, title]) => ({ key, title, ...usage[key] }));
  const creditUsage = usage.agent_credits;
  const creditMeter = creditUsage
    ? {
        key: 'agent_credits',
        title: t('BILLING_SETTINGS.USAGE.AGENT_CREDITS'),
        allowed: creditUsage.cycle_allowance,
        consumed: creditUsage.cycle_consumed,
        remaining: creditUsage.available,
        over_limit: creditUsage.cycle_consumed > creditUsage.cycle_allowance,
      }
    : null;

  return [...capacityMeters, creditMeter].filter(
    meter => meter && Number.isFinite(meter.allowed)
  );
});

const applyTransactionPage = page => {
  transactions.value = page.data;
  transactionPagination.value = page.meta;
};

const fetchBillingData = async () => {
  isLoading.value = true;
  hasLoadError.value = false;
  try {
    const [billingResponse, transactionsResponse] = await Promise.all([
      EnterpriseAccountAPI.getBilling(),
      EnterpriseAccountAPI.getBillingTransactions({ page: 1, perPage: 25 }),
    ]);
    billingData.value = billingResponse.data;
    applyTransactionPage(transactionsResponse.data);
  } catch {
    hasLoadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const fetchTransactionPage = async page => {
  isHistoryLoading.value = true;
  try {
    const response = await EnterpriseAccountAPI.getBillingTransactions({
      page,
      perPage: transactionPagination.value.per_page,
    });
    applyTransactionPage(response.data);
  } catch {
    useAlert(t('BILLING_SETTINGS.HISTORY.LOAD_ERROR'));
  } finally {
    isHistoryLoading.value = false;
  }
};

const onToggleChatWindow = () => {
  window.$chatwoot?.toggle();
};

onMounted(fetchBillingData);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <section class="grid gap-4">
        <BillingCard
          v-if="hasLoadError"
          :title="$t('BILLING_SETTINGS.ERROR.LOAD_FAILED')"
          :description="$t('BILLING_SETTINGS.ERROR.LOAD_DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 sm solid blue @click="fetchBillingData">
              {{ $t('BILLING_SETTINGS.ERROR.RETRY') }}
            </ButtonV4>
          </template>
        </BillingCard>

        <template v-else-if="billingData">
          <BillingCard
            v-if="!subscription"
            :title="$t('BILLING_SETTINGS.NO_PLAN.TITLE')"
            :description="$t('BILLING_SETTINGS.NO_PLAN.DESCRIPTION')"
          />

          <BillingCard
            v-else
            :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
            :description="
              $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')
            "
          >
            <div class="px-5">
              <SubscriptionSummary
                :subscription="subscription"
                :trial="billingData.trial"
                :access="billingData.access"
              />
            </div>
          </BillingCard>

          <BillingCard
            v-if="usageMeters.length"
            :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
            :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
          >
            <div class="grid gap-5 px-5 sm:grid-cols-2">
              <BillingMeter
                v-for="meter in usageMeters"
                :key="meter.key"
                :title="meter.title"
                :allowed="meter.allowed"
                :consumed="meter.consumed"
                :remaining="meter.remaining"
                :over-limit="meter.over_limit"
              />
            </div>
          </BillingCard>

          <BillingCard
            :title="$t('BILLING_SETTINGS.CREDITS.TITLE')"
            :description="$t('BILLING_SETTINGS.CREDITS.DESCRIPTION')"
          >
            <div class="px-5">
              <CreditBalanceBreakdown :credits="billingData.credits" />
            </div>
          </BillingCard>

          <BillingCard
            :title="$t('BILLING_SETTINGS.HISTORY.TITLE')"
            :description="$t('BILLING_SETTINGS.HISTORY.DESCRIPTION')"
          >
            <div class="px-5">
              <BillingHistory
                :transactions="transactions"
                :pagination="transactionPagination"
                :loading="isHistoryLoading"
                @page-change="fetchTransactionPage"
              />
            </div>
          </BillingCard>

          <BillingHeader
            class="mt-5 px-1"
            :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
            :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
          >
            <ButtonV4
              sm
              solid
              slate
              icon="i-ph-lifebuoy"
              @click="onToggleChatWindow"
            >
              {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
            </ButtonV4>
          </BillingHeader>
        </template>
      </section>
    </template>
  </SettingsLayout>
</template>
